import React from 'react';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';

import { type RuleScope, type RuleType } from '../types';

interface RulesHeaderProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  scope: RuleScope;
  onScopeChange: (scope: RuleScope) => void;
  ruleType: RuleType | 'all';
  onRuleTypeChange: (type: RuleType | 'all') => void;
  onAddRule: () => void;
}

export const RulesHeader: React.FC<RulesHeaderProps> = ({
  searchQuery,
  onSearchChange,
  scope,
  onScopeChange,
  ruleType,
  onRuleTypeChange,
  onAddRule,
}) => {
  return (
    <Box sx={{ mb: 4, flexShrink: 0, minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 600, color: 'textColors.darkText' }}
          >
            Rules Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage compliance and learning rules for your organization
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={onAddRule}>
          Add Rule
        </Button>
      </Box>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'minmax(0, 1fr) repeat(2, 150px)' },
        gap: 2,
      }}>
        <TextField
          placeholder="Search rules..."
          value={searchQuery}
          onChange={e => onSearchChange(e.target.value)}
          size="small"
          sx={{ minWidth: 0, gridColumn: { xs: '1 / -1', md: 'auto' } }}
         slotProps={{ input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          } }}/>

        <FormControl size="small" sx={{ minWidth: 0 }}>
          <InputLabel id="rule-scope-label">Scope</InputLabel>
          <Select
            labelId="rule-scope-label"
            value={scope}
            label="Scope"
            onChange={(e: SelectChangeEvent) => onScopeChange(e.target.value as RuleScope)}
          >
            <MenuItem value="both">All Scopes</MenuItem>
            <MenuItem value="global">Global Only</MenuItem>
            <MenuItem value="org">Organization Only</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 0 }}>
          <InputLabel id="rule-type-label">Rule Type</InputLabel>
          <Select
            labelId="rule-type-label"
            value={ruleType}
            label="Rule Type"
            onChange={(e: SelectChangeEvent) =>
              onRuleTypeChange(e.target.value as RuleType | 'all')
            }
          >
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="compliance">Compliance</MenuItem>
            <MenuItem value="learning">Learning</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};
